import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Currencies, CurrencyPipe } from '../../pipe/currency.pipe';
import { InputControlBase } from '../forms/input-control.base';
import { IDataContext } from '../list/component.interface';
import { Money } from '../../model/money';
import { delay, timeout } from 'rxjs/operators';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FormControlEx } from '../forms/form-control';
import { FormControlExFactory } from '../../service/factory/form-control-ex-factory.service';
import { LanguageService } from '../../service/language.service';
import { resolveAstralIconClass } from '../astral-components/astral-icon-class.util';

@Component({
    selector: 'app-money-box',
    templateUrl: './money-box.component.html',
    styleUrls: ['./money-box.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MoneyBoxComponent extends InputControlBase implements IDataContext, OnChanges {
  private static nextId = 0;
  readonly inputId = `money-box-${++MoneyBoxComponent.nextId}`;

  constructor(
    languageService: LanguageService,
    private fcf: FormControlExFactory
  ) {
    super();
    this.pipe = new CurrencyPipe(languageService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.control && this.control.touched) {
      this.displayControl.markAsTouched();
    }
  }

  @Input() dataContext: any;
  @Input() suffix: string;
  @Input() round: boolean = false;

  get readonlyIconClass(): string {
    return resolveAstralIconClass('lock') ?? '';
  }

  get dataContextAmount(): number {
    return this.dataContext ? this.dataContext.amount : null;
  }

  get dataContextCurrency(): string {
    return this.dataContext ? this.dataContext.currencyCode : null;
  }

  displayControl: FormControlEx;
  editControl: FormControlEx;

  onControlSet() {
    super.onControlSet();

    this.displayControl = this.fcf.create(this.control.caption, null, this.proxyValidator(this.control));
    this._updateDisplayValue(this.control.value);

    this.editControl = this.fcf.create(this.control.caption, null, this.proxyValidator(this.control));
    this._updateEditValue(this.control.value);

    if (this.control.required) {
      this.control.addValidators(control => this._validateRequired(control));
    }
  }

  private proxyValidator(control: FormControlEx): ValidatorFn {
    return (c: AbstractControl) => {
      let error = control ? control.getErrorMessage() : null;
      return error;
    };
  }

  pipe: CurrencyPipe;
  private _valueChange: Subscription = new Subscription();
  originalValue: string;

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this._valueChange) {
      this._valueChange.unsubscribe();
      this._valueChange = null;
    }
  }

  private currencyCode: string;

  private _updateDisplayValue(value: Money) {
    if (!value || (!value.amount && value.amount !== 0) || !value.currencyCode || isNaN(value.amount)) {
      this.displayControl.setValue(null);
      return;
    }

    this.displayControl.setValue(this.pipe.transform(value.amount, false, this.suffix, value.currencyCode));
  }

  private _updateEditValue(value: Money) {
    if (!value || (!value.amount && value.amount !== 0) || !value.currencyCode) {
      this.editControl.setValue(null, { emitEvent: false });
      if (value && value.currencyCode) {
        this.currencyCode = value.currencyCode;
      }
      return;
    }
    this.currencyCode = value.currencyCode;
    this.editControl.setValue(value.amount, { emitEvent: false });
  }

  private _duringUpdate: boolean;
  ngOnInit() {
    super.ngOnInit();
    if (this.control) {
      this._valueChange.add(
        this.control.valueChanges.subscribe(value => {
          this._updateDisplayValue(value);
          if (!this._duringUpdate) {
            this._updateEditValue(value);
          }
        })
      );
      this._valueChange.add(
        this.control.touchStateChanged.subscribe(value => {
          if (value) this.displayControl.markAsTouched();
          else this.displayControl.markAsUntouched();
        })
      );
      this._valueChange.add(
        this.editControl.valueChanges.subscribe(value => {
          this._duringUpdate = true;
          let moneyValue = this.pipe.toMoney(value, this.currencyCode);
          this._updateDisplayValue(moneyValue);
          this.control.setValue(moneyValue);
          this.control.markAsDirty();
          this.control.updateValueAndValidity();
          this.editControl.updateValueAndValidity({ emitEvent: false });
          this._duringUpdate = false;
        })
      );
    }
  }

  onblur() {
    super.onblur();
    if (this.control) {
      if (this.round && this.control.value && this.control.value.amount) {
        this.control.setValue(new Money(this.currencyCode, Math.round(this.control.value.amount)));
      }
      this.control.updateValueAndValidity();
      this.displayControl.updateValueAndValidity();
    }
    this.hasFocus = false;
  }

  hasFocus: boolean = false;
  onfocus() {
    super.onfocus();
    if (this.control && !this.control.isReadonly) {
      this.hasFocus = true;
      this.control.markAsTouched();
      this.displayControl.markAsTouched();
    }
  }

  getErrorMessage(): { message: string; params: any } {
    if (this.control.invalid) {
      if (this.control.touched && !this.displayControl.touched) {
        this.displayControl.markAsTouched();
      }
      let error = this.control.getErrorMessage();
      if (!error || !error.message) throw new Error('Error was not specified.');

      return error;
    }
    return null;
  }

  private _validateRequired(control: AbstractControl): ValidationErrors {
    if (!this.control.value || (!this.control.value.amount && this.control.value.amount !== 0) || !this.control.value.currencyCode) {
      return { function: { message: 'ERROR.REQUIRED', params: {} } };
    }
    return null;
  }
}
